import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchSubmissions,
  upsertSubmission,
  deleteSubmission,
  toggleSubmissionLike,
} from '../lib/queries';
import { useSession } from '../lib/session';
import { authorHandle } from '../lib/display';
import { useToast } from './Toast';
import { SubmissionModal } from './SubmissionModal';
import { SubmissionComposer } from './SubmissionComposer';
import type { Submission } from '../lib/types';
import './RedesignSubmissions.css';

interface Props {
  /** The Redesign Challenge post these submissions belong to. */
  postId: string;
}

/** Sort most-liked first, then newest - mirrors the server ordering so a
 *  locally-mutated like re-ranks the grid the same way a refetch would. */
function rankSubmissions(list: Submission[]): Submission[] {
  return [...list].sort((a, b) => {
    if (b.like_count !== a.like_count) return b.like_count - a.like_count;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Member-facing submissions area for a Redesign Challenge post. Renders a
 * responsive grid of submission tiles (image + like), a detail modal, and a
 * one-editable-per-member submit/edit composer. Reads the viewer from
 * useSession, so the only prop needed is the post id - mounts in one line.
 */
export function RedesignSubmissions({ postId }: Props) {
  const { profile } = useSession();
  const toast = useToast();
  const viewerId = profile?.id ?? null;
  const isStaff = profile?.role === 'admin' || profile?.role === 'developer';

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  // Guards optimistic like writes against races: each submission can only
  // have one in-flight toggle at a time.
  const likeInFlight = useRef<Set<string>>(new Set());

  // Load on mount / when post or viewer changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    fetchSubmissions(postId, viewerId, ctrl.signal)
      .then((rows) => {
        setSubmissions(rankSubmissions(rows));
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        console.error('[redesign-submissions] load failed', err);
        setError(true);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [postId, viewerId]);

  const mySubmission = useMemo(
    () => (viewerId ? submissions.find((s) => s.author_id === viewerId) ?? null : null),
    [submissions, viewerId],
  );

  const openSubmission = useMemo(
    () => submissions.find((s) => s.id === openId) ?? null,
    [submissions, openId],
  );

  const handleLike = useCallback(
    async (target: Submission) => {
      if (!viewerId) {
        toast.error('Sign in to like designs.');
        return;
      }
      if (likeInFlight.current.has(target.id)) return;
      likeInFlight.current.add(target.id);

      const wasLiked = !!target.viewer_liked;
      // Optimistic: flip viewer_liked + adjust count, then re-rank.
      setSubmissions((prev) =>
        rankSubmissions(
          prev.map((s) =>
            s.id === target.id
              ? { ...s, viewer_liked: !wasLiked, like_count: s.like_count + (wasLiked ? -1 : 1) }
              : s,
          ),
        ),
      );

      try {
        const next = await toggleSubmissionLike(target.id, viewerId, wasLiked);
        // Reconcile with the server's truth (in case state drifted).
        setSubmissions((prev) =>
          rankSubmissions(prev.map((s) => (s.id === target.id ? { ...s, viewer_liked: next } : s))),
        );
      } catch (err) {
        console.error('[redesign-submissions] like failed', err);
        // Rollback.
        setSubmissions((prev) =>
          rankSubmissions(
            prev.map((s) =>
              s.id === target.id
                ? { ...s, viewer_liked: wasLiked, like_count: s.like_count + (wasLiked ? 1 : -1) }
                : s,
            ),
          ),
        );
        toast.error('Could not update your like.');
      } finally {
        likeInFlight.current.delete(target.id);
      }
    },
    [viewerId, toast],
  );

  const handleSave = useCallback(
    async ({ imageUrl, body }: { imageUrl: string; body: string }) => {
      if (!viewerId) return;
      const saved = await upsertSubmission({ postId, authorId: viewerId, imageUrl, body });
      // upsertSubmission returns viewer_liked=false; preserve any prior like.
      const prior = submissions.find((s) => s.author_id === viewerId);
      const merged: Submission = { ...saved, viewer_liked: prior?.viewer_liked ?? false };
      setSubmissions((prev) => {
        const without = prev.filter((s) => s.id !== merged.id && s.author_id !== viewerId);
        return rankSubmissions([...without, merged]);
      });
      setComposerOpen(false);
      toast.success(prior ? 'Your design was updated.' : 'Your design was submitted.');
    },
    [postId, viewerId, submissions, toast],
  );

  // Shared by the viewer's own card and the admin path (RLS allows
  // delete for the author and for admin/developer roles).
  const removeSubmission = useCallback(
    async (removed: Submission) => {
      const isMine = removed.author_id === viewerId;
      // Optimistic remove.
      setSubmissions((prev) => prev.filter((s) => s.id !== removed.id));
      setOpenId((prev) => (prev === removed.id ? null : prev));
      try {
        await deleteSubmission(removed.id);
        toast.success(isMine ? 'Your design was removed.' : 'Design removed.');
      } catch (err) {
        console.error('[redesign-submissions] remove failed', err);
        setSubmissions((prev) => rankSubmissions([...prev, removed]));
        toast.error(isMine ? 'Could not remove your submission.' : 'Could not remove the design.');
      }
    },
    [viewerId, toast],
  );

  const handleRemove = useCallback(() => {
    if (mySubmission) void removeSubmission(mySubmission);
  }, [mySubmission, removeSubmission]);

  const handleAdminRemove = useCallback(
    (target: Submission) => {
      const ok = window.confirm(
        `Remove @${authorHandle(target.author)}'s design? This permanently deletes their submission and can't be undone.`,
      );
      if (!ok) return;
      void removeSubmission(target);
    },
    [removeSubmission],
  );

  return (
    <section className="redesign" aria-labelledby="redesign-heading">
      <div className="redesign__head">
        <h2 id="redesign-heading" className="redesign__title">Member designs</h2>
        {viewerId && !mySubmission && !loading && !error && (
          <button
            type="button"
            className="btn btn--accent btn--sm redesign__submit"
            onClick={() => setComposerOpen(true)}
          >
            Submit your design
          </button>
        )}
      </div>

      {viewerId && mySubmission && (
        <div className="redesign__mine">
          <img className="redesign__mine-thumb" src={mySubmission.image_url} alt="Your submission" />
          <div className="redesign__mine-body">
            <p className="redesign__mine-label">Your submission</p>
            {mySubmission.body && <p className="redesign__mine-text">{mySubmission.body}</p>}
          </div>
          <div className="redesign__mine-actions">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setComposerOpen(true)}>
              Edit
            </button>
            <button type="button" className="redesign__remove" onClick={handleRemove}>
              Remove
            </button>
          </div>
        </div>
      )}

      {loading && <p className="redesign__status">Loading designs…</p>}

      {!loading && error && (
        <p className="redesign__status redesign__status--error">
          Couldn’t load submissions. Please refresh.
        </p>
      )}

      {!loading && !error && submissions.length > 0 && (
        <ul className="redesign__grid">
          {submissions.map((s) => {
            const liked = !!s.viewer_liked;
            return (
              <li key={s.id} className="redesign__tile">
                <button
                  type="button"
                  className="redesign__tile-btn"
                  onClick={() => setOpenId(s.id)}
                  aria-label={`Open design by ${authorHandle(s.author)}`}
                >
                  <img
                    className="redesign__tile-img"
                    src={s.image_url}
                    alt={`Design by ${authorHandle(s.author)}`}
                    loading="lazy"
                  />
                </button>
                <button
                  type="button"
                  className={`pill-action pill-action--overlay pill-action--sm redesign__tile-like ${liked ? 'pill-action--on' : ''}`}
                  onClick={() => handleLike(s)}
                  disabled={!viewerId}
                  aria-pressed={liked}
                  aria-label={liked ? 'Unlike this design' : 'Like this design'}
                >
                  <span aria-hidden="true">{liked ? '♥' : '♡'}</span>
                  <span>{s.like_count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {openSubmission && (
        <SubmissionModal
          submission={openSubmission}
          canLike={!!viewerId}
          onLike={handleLike}
          onRemove={isStaff ? handleAdminRemove : undefined}
          onClose={() => setOpenId(null)}
        />
      )}

      {composerOpen && viewerId && (
        <SubmissionComposer
          userId={viewerId}
          existing={mySubmission}
          onSave={handleSave}
          onClose={() => setComposerOpen(false)}
        />
      )}
    </section>
  );
}
